// path: frontend/src/hooks/useTaskSelectOptions.js
/**
 * Опции для селектов в форме задачи.
 * assigneeOptionsList: формируется на основе GET /employees/assignable
 * (useAssignableEmployeesQuery). Сервер возвращает только тех, кому текущий
 * юзер имеет право назначить задачу (canAssignTo).
 */
import { useMemo } from "react";
import {
  PRIORITIES,
  TASK_STATUSES,
  DEPENDENCY_TYPES,
} from "../utils/constants";
import { useAssignableEmployeesQuery } from "../api/queries/employees";

const DEFAULT_NEW_STATUSES = Object.freeze(["draft", "new"]);

export function useTaskSelectOptions({
  db,
  isProjectLocked,
  effectiveProjectId,
  taskId,
  projectId,
}) {
  const { data: assignableEmployees = [] } = useAssignableEmployeesQuery();

  const currentTask = useMemo(
    () => (taskId ? db.tasks.find((t) => t.id === taskId) || null : null),
    [db.tasks, taskId],
  );

  const projectOptions = useMemo(() => {
    const filtered = db.projects.filter(
      (p) => p.status === "active" && !p.archived,
    );
    const options = [
      { value: "", label: "- Выберите проект -" },
      ...filtered.map((p) => ({
        value: p.id,
        label: `${p.code} - ${p.name}${p.ptype === "admin" ? " (административный)" : ""}`,
      })),
    ];
    if (
      currentTask?.projectId &&
      !options.some((o) => o.value === currentTask.projectId)
    ) {
      const proj = db.projects.find((p) => p.id === currentTask.projectId);
      if (proj)
        options.push({
          value: proj.id,
          label: `${proj.code} - ${proj.name} (архивный проект)`,
        });
    }
    if (
      isProjectLocked &&
      effectiveProjectId &&
      !options.some((o) => o.value === effectiveProjectId)
    ) {
      const proj = db.projects.find((p) => p.id === effectiveProjectId);
      if (proj)
        options.push({
          value: proj.id,
          label: `${proj.code} - ${proj.name}${proj.ptype === "admin" ? " (административный)" : ""}`,
        });
    }
    return options;
  }, [db, isProjectLocked, effectiveProjectId, currentTask]);

  const assigneeOptionsList = useMemo(() => {
    const list = [...assignableEmployees];
    if (currentTask?.assigneeId) {
      const current = db.employees.find((e) => e.id === currentTask.assigneeId);
      if (current && !list.some((e) => e.id === current.id)) list.push(current);
    }
    return [
      { value: "", label: "- Выберите исполнителя -" },
      ...list.map((e) => ({
        value: e.id,
        label: `${e.last} ${e.first}`,
      })),
    ];
  }, [assignableEmployees, currentTask, db.employees]);

  const priorityOptions = useMemo(
    () =>
      Object.entries(PRIORITIES).map(([k, v]) => ({
        value: k,
        label: v.label,
      })),
    [],
  );

  const statusOptions = useMemo(() => {
    const allowed = currentTask?.permissions?.allowedStatuses;
    const list = Array.isArray(allowed)
      ? allowed
      : currentTask
        ? [currentTask.status]
        : DEFAULT_NEW_STATUSES;
    const unique = [...new Set([currentTask?.status, ...list].filter(Boolean))];
    return unique.map((s) => ({
      value: s,
      label: TASK_STATUSES[s]?.label || s,
    }));
  }, [currentTask]);

  const dependencyOptions = useMemo(() => {
    const list = db.tasks
      .filter(
        (t) =>
          t.id !== taskId &&
          t.projectId === projectId &&
          t.status !== "closed" &&
          t.status !== "cancelled",
      )
      .map((t) => ({ value: t.id, label: t.title }));
    return [{ value: "", label: "-" }, ...list];
  }, [db.tasks, taskId, projectId]);

  const dependencyTypeOptions = useMemo(
    () =>
      Object.entries(DEPENDENCY_TYPES).map(([k, v]) => ({
        value: k,
        label: `${v.label} - ${v.desc}`,
      })),
    [],
  );

  const candidates = useMemo(() => {
    const ids = new Set(
      db.tasks
        .filter((t) => t.projectId === projectId)
        .map((t) => t.assigneeId)
        .filter(Boolean),
    );
    const pj = db.projects.find((p) => p.id === projectId);
    if (pj && pj.managerId) ids.add(pj.managerId);
    return [...ids]
      .map((id) => db.employees.find((e) => e.id === id))
      .filter(Boolean);
  }, [db, projectId]);

  return {
    projectOptions,
    assigneeOptionsList,
    priorityOptions,
    statusOptions,
    dependencyOptions,
    dependencyTypeOptions,
    candidates,
  };
}
